import type {
  TicketDetailArtifact,
  TicketDetailAttempt,
  TicketDetailEvidence,
} from '@nightshift/ui-types';

import { calendarDate } from './format.ts';

/**
 * How the evidence drawer decides what to draw, and how loudly.
 *
 * Pure, and separate from the component, because the interesting decisions are arithmetic:
 * what the tab badge counts, which artifact rows are still live, and when a kind has enough
 * rows that a summary line serves a reviewer better than the rows themselves. CELL-28 showed
 * 349 items in a tab whose useful content was twelve evidence rows and a handful of live
 * artifacts; every rule here exists to stop that number being the first thing anyone reads.
 *
 * Nothing here changes what is stored or harvested. Duplicate CI artifacts are still written
 * once per attempt; this module only stops the drawer reciting all of them.
 */

/**
 * Rows above which a kind collapses to one summary line.
 *
 * Five, to match what the drawer already treats as a glanceable list: the required-verification
 * block runs to about five checks and reads fine, and the artifact kinds that stay small in
 * practice (`plan`, `ci-timings`, `verification-result`, the review verdicts) are one or two
 * rows each. The kinds that blow past it are the per-scenario CI harvests — `capture`,
 * `verification-proof`, `launch`, which arrive 40 to 80 at a time and are individually
 * uninteresting. Any threshold in the 4-to-8 range separates those two populations; 5 is the
 * low end of that range, so a group only collapses once it is genuinely a list rather than a
 * handful.
 */
export const ARTIFACT_GROUP_COLLAPSE_THRESHOLD = 5;

/**
 * Whether an artifact still describes the current head.
 *
 * `superseded` already implies `stale` in the ticket-detail payload (core sets the flag and
 * the reason together), but both are asked here so a payload that ever separates them cannot
 * quietly put a superseded row back in the live list.
 */
export function isLiveArtifact(row: TicketDetailArtifact): boolean {
  return !row.stale && !row.superseded;
}

/** Verification evidence carries no `superseded` column; staleness is the whole test. */
function isLiveEvidence(row: TicketDetailEvidence): boolean {
  return !row.stale;
}

/**
 * The number on the Evidence tab.
 *
 * Live rows only. A badge that counts superseded work reports the size of the table rather
 * than the size of the reviewer's job, and on a ticket with three attempts the table is
 * mostly the first two attempts.
 */
export function evidenceBadgeCount(input: {
  artifacts: TicketDetailArtifact[];
  evidence: TicketDetailEvidence[];
}): number {
  return (
    input.artifacts.filter(isLiveArtifact).length + input.evidence.filter(isLiveEvidence).length
  );
}

/** Artifacts split into what is current and what an earlier attempt left behind. */
export function partitionArtifacts(rows: TicketDetailArtifact[]): {
  live: TicketDetailArtifact[];
  stale: TicketDetailArtifact[];
} {
  const live: TicketDetailArtifact[] = [];
  const stale: TicketDetailArtifact[] = [];
  for (const row of rows) {
    if (isLiveArtifact(row)) live.push(row);
    else stale.push(row);
  }
  return { live, stale };
}

export interface ArtifactGroup {
  kind: string;
  rows: TicketDetailArtifact[];
  /** True when the group is long enough to be drawn as one summary line first. */
  collapsed: boolean;
  /**
   * Short head SHAs the group's rows were captured against, first seen first. Empty when no
   * attempt in the payload names a head — a plan artifact written before any MR exists.
   */
  heads: string[];
}

/** Attempt id to its merge-request head, for labelling a group with the SHA it belongs to. */
function attemptHeads(attempts: TicketDetailAttempt[]): Map<number, string> {
  const heads = new Map<number, string>();
  for (const attempt of attempts) {
    if (attempt.mrHeadSha) heads.set(attempt.id, attempt.mrHeadSha);
  }
  return heads;
}

/**
 * Group artifacts by kind, keeping the payload's own order.
 *
 * Kinds appear in the order their first row does (core orders by `created_at`, oldest first),
 * and rows keep that order inside a group, so expanding a summary shows the same sequence the
 * flat list used to.
 */
export function groupArtifactsByKind(
  rows: TicketDetailArtifact[],
  attempts: TicketDetailAttempt[] = [],
  threshold: number = ARTIFACT_GROUP_COLLAPSE_THRESHOLD,
): ArtifactGroup[] {
  const heads = attemptHeads(attempts);
  const groups = new Map<string, ArtifactGroup>();
  for (const row of rows) {
    let group = groups.get(row.kind);
    if (!group) {
      group = { kind: row.kind, rows: [], collapsed: false, heads: [] };
      groups.set(row.kind, group);
    }
    group.rows.push(row);
    const head = heads.get(row.attemptId);
    if (head) {
      const short = head.slice(0, 8);
      if (!group.heads.includes(short)) group.heads.push(short);
    }
  }
  const out = [...groups.values()];
  for (const group of out) group.collapsed = group.rows.length > threshold;
  return out;
}

/**
 * The SHA label on a collapsed group: one head named, several counted.
 *
 * A group spanning two heads is normal on a ticket whose artifacts were harvested either side
 * of a push, and naming only the first would be a lie about the rest.
 */
export function artifactGroupHeadLabel(group: ArtifactGroup): string | null {
  if (group.heads.length === 0) return null;
  if (group.heads.length === 1) return group.heads[0] ?? null;
  return `${group.heads.length} heads`;
}

/** "82 records at 79fe70e9" — the whole of what a collapsed group says before it is opened. */
export function artifactGroupSummary(group: ArtifactGroup): string {
  const count = `${group.rows.length} record${group.rows.length === 1 ? '' : 's'}`;
  const head = artifactGroupHeadLabel(group);
  return head === null ? count : `${count} at ${head}`;
}

/**
 * What an evidence image's place says when its file cannot be fetched.
 *
 * Retention removes evidence files a while after their ticket finished, keeping the row. That
 * is policy rather than a fault, so it is said in words instead of a broken-image icon.
 */
export function missingEvidenceNote(expiredAt: number | null): string {
  return expiredAt === null
    ? 'Image unavailable.'
    : `Removed under the retention policy; it expired on ${calendarDate(expiredAt)}.`;
}
